import React from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box } from '@mui/material';
import { Download } from '@mui/icons-material';
import { CSVData } from '../utils/csvParser';
import { downloadService } from '../services/downloadService';

interface NetworkSummaryTableProps {
  data: CSVData[];
  fontFamily?: string;
}

export const NetworkSummaryTable: React.FC<NetworkSummaryTableProps> = ({ data, fontFamily = 'JetBrains Mono' }) => {
  const formatNumber = (num: number) => num.toLocaleString('en-IN');

  // Calculate totals
  const totalCalls = data.reduce((sum, row) => sum + (Number(row.total_calls) || 0), 0);
  const totalRcCalls = data.reduce((sum, row) => sum + (Number(row.total_rc_calls) || 0), 0);
  const totalMappingCalls = data.reduce((sum, row) => sum + (Number(row.total_mapping_calls) || 0), 0);
  const totalSuccess = data.reduce((sum, row) => sum + (Number(row.success) || 0), 0);
  const totalRcSuccess = data.reduce((sum, row) => sum + (Number(row.rc_success) || 0), 0);
  const totalMappingSuccess = data.reduce((sum, row) => sum + (Number(row.mapping_success) || 0), 0);
  const totalDataWithoutMapping = data.reduce((sum, row) => sum + (Number(row.data_without_mapping) || 0), 0);
  const totalFailed = data.reduce((sum, row) => sum + (Number(row.failed) || 0), 0);

  const handleDownload = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    downloadService.downloadTableAsCSV(data, `network_api_summary_${timestamp}.csv`);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3, fontFamily }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily }}>
          🌐 Third Party API Summary
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownload}
          size="small"
          sx={{ fontFamily }}
        >
          Download CSV
        </Button>
      </Box>
      
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Client Name</TableCell>
              <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Sub Product</TableCell>
              <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Destination System</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Total Calls</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Total RC Calls</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Total Mapping Calls</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Success</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>RC Success</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Mapping Success</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Data Without Mapping</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Failed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ fontFamily }}>No data available</TableCell>
              </TableRow>
            ) : (
              <>
                {data.map((row, index) => {
                  const isChallan = String(row.sub_product).toLowerCase().includes('challan');
                  const isSurepass = String(row.destination_system).toLowerCase().includes('surepass');
                  return (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontFamily }}>{row.client_name}</TableCell>
                      <TableCell sx={{ fontFamily }}>{row.sub_product}</TableCell>
                      <TableCell sx={{ fontFamily }}>{row.destination_system}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{formatNumber(Number(row.total_calls) || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{isChallan ? 'NA' : (isSurepass ? formatNumber(Number(row.total_rc_calls)) : 'NA')}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{isChallan ? 'NA' : (isSurepass ? formatNumber(Number(row.total_mapping_calls)) : 'NA')}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{formatNumber(Number(row.success) || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{isChallan ? 'NA' : (isSurepass ? formatNumber(Number(row.rc_success)) : 'NA')}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{isChallan ? 'NA' : (isSurepass ? formatNumber(Number(row.mapping_success)) : 'NA')}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{isChallan ? 'NA' : formatNumber(Number(row.data_without_mapping) || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily }}>{formatNumber(Number(row.failed) || 0)}</TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                  <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>All</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>All</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalCalls)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalRcCalls)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalMappingCalls)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalSuccess)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalRcSuccess)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalMappingSuccess)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalDataWithoutMapping)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalFailed)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}; 